import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const FAQSection = () => {
  const mainFAQs = [
    {
      question: 'Je to předplatné? Budete mi strhávat peníze každý měsíc?',
      answer: 'Ne. Rozhodně ne. Zaplatíte jednou za vybraný balíček a tím to končí. Žádné automatické stržení, žádná "překvapení" na výpisu.',
    },
    {
      question: 'Kdy to celé začne?',
      answer: 'Jakmile dorazí platba, systém se probudí a začne pracovat. U párové výzvy začnou chodit SMS podle nastavení. U rozlučkové výzvy obdržíte balíček připravený k akci.',
    },
    {
      question: 'Co když zadám špatné číslo?',
      answer: 'SMS pak půjde někomu jinému. A to by byla docela podivná situace. Doporučujeme zkontrolovat čísla. Pokud se něco nepovede, napište nám co nejdříve.',
    },
    {
      question: 'Musí o tom druhá osoba vědět?',
      answer: 'Ano. Nejsme tajná služba. Pokud zadáváte číslo někoho jiného, musíte mít jeho souhlas a tato osoba musí být starší 18 let.',
    },
    {
      question: 'Je to trapné?',
      answer: 'Záleží, jak moc se otevřete. Výzva je zábavná, nenásilná a vždy dobrovolná, takže se dá dělat v tempu, které vám vyhovuje.',
    },
    {
      question: 'Je to terapie?',
      answer: 'Ne. Je to hra. Pokud hledáte párového terapeuta, tady ho nenajdete. Pokud chcete rozhybat stereotyp, jste na správném místě.',
    },
    {
      question: 'Dostanu potvrzení o platbě?',
      answer: 'Ano. Po platbě obdržíte potvrzení a daňový doklad. Účetní klid zachován.',
    },
    {
      question: 'Mohu to zrušit po zaplacení?',
      answer: 'U digitálních služeb začíná plnění po platbě. Jakmile systém začne odesílat SMS nebo vygeneruje balíček, odstoupení již není možné. Takže si výběr v klidu zvažte.',
    },
  ];

  const bonusFAQs = [
    {
      question: 'Co když se během výzvy pohádáme?',
      answer: 'Pak jste alespoň něco cítili. Výzva má rozhybat dynamiku, ne vás zabalit do bublinkové fólie.',
    },
    {
      question: 'Co když je jeden z nás líný jako po nedělním obědu?',
      answer: 'Pak ho to konečně zvedne. Výzva není maraton, je to malý šťouch do stereotypu.',
    },
    {
      question: 'Co když se někdo urazí a začne dělat drama queen?',
      answer: 'Drama queen si důvod najde i u těsta na pizzu. Výzvy jsou přehnané. Kdo se chce urazit, urazí se i bez nás.',
    },
    {
      question: 'Co když to nebude tak epické, jak si představuji?',
      answer: 'Tohle není hollywoodská produkce. Ale pokud do toho dáte energii, dostanete zpět víc, než čekáte.',
    },
  ];

  return (
    <section id="faq" data-testid="faq-section" className="py-20 section-warm">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-semibold text-[var(--vp-ink)] mb-4">
            Často kladené otázky
          </h2>
          <p className="text-[var(--vp-ink)]/70 max-w-2xl mx-auto">
            Všechno, co potřebujete vědět, na jednom místě.
          </p>
        </motion.div>

        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible data-testid="faq-accordion">
            {mainFAQs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <AccordionItem
                  value={`item-${index}`}
                  className="faq-item"
                  data-testid="faq-accordion-item"
                >
                  <AccordionTrigger
                    className="px-6 py-4 text-left hover:no-underline"
                    data-testid="faq-accordion-trigger"
                  >
                    <span className="font-medium text-[var(--vp-ink)]">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent
                    className="px-6 pb-4"
                    data-testid="faq-accordion-content"
                  >
                    <p className="text-[var(--vp-ink)]/70 leading-relaxed">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              </motion.div>
            ))}
          </Accordion>

          {/* Bonus FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="mt-12"
          >
            <h3 className="text-xl font-semibold text-[var(--vp-ink)] mb-6 text-center">
              Bonus FAQ 🌟
            </h3>
            <Accordion type="single" collapsible>
              {bonusFAQs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`bonus-${index}`}
                  className="faq-item bg-[var(--vp-sand)]/50"
                >
                  <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                    <span className="font-medium text-[var(--vp-ink)]">
                      {faq.question}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-[var(--vp-ink)]/70 leading-relaxed">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
